import { z } from 'zod';

/**
 * AI-Assistant conversation persistence (W7 #7).  Conversations + messages are
 * private to the owner; these DTOs are the wire contract between the web /ai
 * page and the `/api/ai/conversations` endpoints.
 */

export const AssistantMessageRoleSchema = z.enum(['user', 'assistant']);
export type AssistantMessageRole = z.infer<typeof AssistantMessageRoleSchema>;

/** One persisted message in a conversation. */
export const AssistantMessageSchema = z.object({
  id: z.string().uuid(),
  clientMessageId: z.string().uuid().nullable(),
  sequence: z.number().int().positive(),
  role: AssistantMessageRoleSchema,
  content: z.string(),
  /** Free-form extras for assistant turns (citations, usage). */
  metadata: z.record(z.unknown()).nullable(),
  createdAt: z.string(),
});
export type AssistantMessage = z.infer<typeof AssistantMessageSchema>;

/** Conversation row without messages — for the left-rail list. */
export const AssistantConversationSummarySchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  teamId: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  lastMessageAt: z.string().nullable(),
  messageCount: z.number().int().nonnegative(),
});
export type AssistantConversationSummary = z.infer<typeof AssistantConversationSummarySchema>;

/** Conversation + its messages — for loading a thread. */
export const AssistantConversationDetailSchema = AssistantConversationSummarySchema.extend({
  messages: z.array(AssistantMessageSchema),
});
export type AssistantConversationDetail = z.infer<typeof AssistantConversationDetailSchema>;

export const AssistantConversationListSchema = z.object({
  conversations: z.array(AssistantConversationSummarySchema),
});
export type AssistantConversationList = z.infer<typeof AssistantConversationListSchema>;

// ── Inputs ───────────────────────────────────────────────────────────────────

export const CreateAssistantConversationSchema = z.object({
  title: z.string().min(1).max(200).optional(),
  teamId: z.string().uuid().optional(),
  /** Stable retry key generated once by the client. */
  clientConversationId: z.string().uuid().optional(),
});
export type CreateAssistantConversationInput = z.infer<typeof CreateAssistantConversationSchema>;

export const RenameAssistantConversationSchema = z.object({
  title: z.string().min(1).max(200),
});
export type RenameAssistantConversationInput = z.infer<typeof RenameAssistantConversationSchema>;

export const AppendAssistantMessageSchema = z.object({
  role: AssistantMessageRoleSchema,
  content: z.string().min(1).max(100000),
  metadata: z.record(z.unknown()).optional(),
  /** Stable retry key generated once per local message/draft. */
  clientMessageId: z.string().uuid().optional(),
});
export type AppendAssistantMessageInput = z.infer<typeof AppendAssistantMessageSchema>;

/** Frontend state contract; `unsaved` retains a retryable local draft. */
export const AssistantPersistenceStateSchema = z.enum(['saving', 'saved', 'unsaved', 'failed']);
export type AssistantPersistenceState = z.infer<typeof AssistantPersistenceStateSchema>;
